import React from "react";
import { cx } from "utils/join-classnames";
import styles from "./RangeSlider.module.scss";

interface Props {
    value: number;
    maxValue?: number;
    inverse?: boolean;
    onChange?(value: number): void;
    onDraggingEnd(): void;
    className?: string;
    enabled?: boolean;
}

const RangeSlider: React.FC<Props> = ({
    inverse = false,
    onChange = null,
    onDraggingEnd,
    maxValue = 100,
    className = null,
    enabled = true,
    value = 0,
}) => {
    const [showMouseTimecode, setShowMouseTimecode] =
        React.useState<boolean>(false);
    const timecodeRef = React.useRef<HTMLDivElement>(null);
    const trackRef = React.useRef<HTMLDivElement>(null);
    const [labelText, setLabelText] = React.useState<string>("");

    const eachDrag = (e: React.TouchEvent | React.MouseEvent) => {
        e.stopPropagation();
    };

    const onSlide = (e: React.ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation();
        if (onChange) {
            onChange(+e.target.value);
        }
    };

    const handleTrackEnter = () => {
        setShowMouseTimecode(true);
    };
    const handleTrackLeave = () => {
        setShowMouseTimecode(false);
    };

    const onMoveTimecode = (e: MouseEvent) => {
        if (!timecodeRef.current || !trackRef.current) {
            return;
        }
        const mousePosOnTrack =
            e.clientX - trackRef.current.getBoundingClientRect().left;
        const trackWidth = trackRef.current.getBoundingClientRect().width;
        timecodeRef.current.style.left = `${mousePosOnTrack}px`;
        const pointerPerc = Math.round((mousePosOnTrack / trackWidth) * 100);
        const pointerPercInverse = 100 - pointerPerc;
        const fakeVal = !inverse
            ? (maxValue / 100000) * pointerPerc
            : (maxValue / 100000) * pointerPercInverse;
        setLabelText(`${fakeVal.toFixed(2)}s/${(maxValue / 1000).toFixed(2)}s`);
    };

    React.useEffect(() => {
        if (!showMouseTimecode) {
            return undefined;
        }
        document.body.addEventListener("mousemove", onMoveTimecode);
        return () =>
            document.body.removeEventListener("mousemove", onMoveTimecode);
    }, [showMouseTimecode]);
    React.useEffect(() => {
        setLabelText(
            `${(value / 1000).toFixed(2)}s/${(maxValue / 1000).toFixed(2)}s`,
        );
    }, []);

    const containerClasses = cx(
        styles.container,
        className,
        inverse ? styles.inverted : null,
        enabled ? styles.enabled : null,
    );

    const perc = (value / maxValue) * 100;
    const sliderStyle = !inverse
        ? {
              background: `linear-gradient(90deg, #4d5765  0%, #4d5765 ${perc}%, #2f3741 ${perc}%)`,
          }
        : {
              background: `linear-gradient(to left, #4d5765  0%, #4d5765 ${perc}%, #2f3741 ${perc}%)`,
          };
    return (
        <div
            className={containerClasses}
            onMouseEnter={handleTrackEnter}
            onMouseLeave={handleTrackLeave}
            ref={trackRef}
        >
            {showMouseTimecode && (
                <div className={styles.mousetimecode} ref={timecodeRef}>
                    {labelText}
                </div>
            )}
            <input
                type="range"
                min="0"
                max={maxValue}
                value={value}
                onInput={onSlide}
                className={styles.slider}
                style={sliderStyle}
                onMouseUp={onDraggingEnd}
                onTouchStart={eachDrag}
                onMouseDown={eachDrag}
            />
        </div>
    );
};
export default RangeSlider;
